import React, { useEffect, useState, useCallback } from "react";
import { brl } from "../lib";

const CAMPOS = [
  { key: "custo_materiais", label: "Custo de materiais" },
  { key: "custo_medicoes", label: "Custo de medições" },
  { key: "rt_arquiteto", label: "RT do arquiteto" },
  { key: "impostos", label: "Impostos" },
  { key: "fretes", label: "Fretes" },
  { key: "colocacao", label: "Colocação / instalação" },
  { key: "retrabalhos", label: "Retrabalhos" },
  { key: "outros", label: "Outros custos" },
];

// Input type=number entrega "7250.59" (ponto decimal). Só trata ponto como
// milhar quando há vírgula (formato BR digitado/colado, ex: "7.250,59").
export const toNum = (raw) => {
  if (typeof raw === "number") return raw;
  const s = String(raw ?? "").trim();
  if (!s) return 0;
  const n = s.includes(",") ? parseFloat(s.replace(/\./g, "").replace(",", ".")) : parseFloat(s);
  return isNaN(n) ? 0 : n;
};

/**
 * Painel Lucro Real (interno). NUNCA renderizado pra cliente/arquiteto — a
 * tab só aparece pra admin/gestor/financeiro no App.js. Backend também bloqueia.
 * Grava os 7 custos + mostra Receita (base + aditivos aprovados), Lucro e Margem.
 */
export default function LucroRealPanel({ obraId, api }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const load = useCallback(async () => {
    const r = await api("get", `/obras/${obraId}/lucro-real`);
    setData(r);
    setForm(r.custos);
    setDirty(false);
  }, [api, obraId]);
  useEffect(() => { load(); }, [load]);

  const change = (key, v) => {
    setForm(f => ({ ...f, [key]: v }));
    setDirty(true);
  };

  const salvar = async () => {
    setSaving(true);
    try {
      // Normaliza strings tipo "1.234,56" para número
      const payload = {};
      for (const c of CAMPOS) payload[c.key] = toNum(form[c.key]);
      const r = await api("put", `/obras/${obraId}/lucro-real`, payload);
      setData(r);
      setForm(r.custos);
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div style={{ padding: 24, color: "var(--ink-3)" }}>Carregando…</div>;

  // Preview em tempo real do lucro/margem enquanto o admin digita
  const totalCustosPreview = CAMPOS.reduce((s, c) => s + toNum(form[c.key]), 0);
  const lucroPreview = data.receita - totalCustosPreview;
  const margemPreview = data.receita > 0 ? (lucroPreview / data.receita * 100) : 0;
  const margemCor = margemPreview >= 30 ? "#5fa86c" : margemPreview >= 15 ? "#b8935a" : "#c47a7a";

  return (
    <div data-testid="lucro-real-panel">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "#c47a7a" }}>
          🔒 Interno · não aparece pro cliente
        </div>
        <h2 className="page-title" style={{ marginTop: 4 }}>Lucro Real</h2>
      </div>

      {/* KPIs no topo */}
      <div className="fin-grid" style={{ marginBottom: 28 }}>
        <div className="fin-box">
          <div className="fin-label">Receita aprovada</div>
          <div className="fin-value" data-testid="lucro-receita">{brl(data.receita)}</div>
          <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink-3)", marginTop: 4 }}>
            Base {brl(data.orcamento_base)} + Serviços Adicionais {brl(data.aditivos_aprovados)}
          </div>
        </div>
        <div className="fin-box">
          <div className="fin-label">Lucro real</div>
          <div className="fin-value" data-testid="lucro-valor" style={{ color: lucroPreview >= 0 ? "#5fa86c" : "#c47a7a" }}>{brl(lucroPreview)}</div>
          <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink-3)", marginTop: 4 }}>
            Custos totais {brl(totalCustosPreview)}
          </div>
        </div>
        <div className="fin-box highlight" style={{ borderColor: margemCor }}>
          <div className="fin-label">Margem</div>
          <div className="fin-value" data-testid="lucro-margem" style={{ color: margemCor, fontSize: 40 }}>
            {margemPreview.toFixed(1)}<span style={{ fontSize: 22 }}>%</span>
          </div>
        </div>
      </div>

      {/* Formulário de custos */}
      <div className="panel">
        <div className="panel-head">
          <div>
            <div className="panel-eyebrow">Custos reais</div>
            <h3 className="panel-title">Detalhamento</h3>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {savedFlash && <span style={{ fontSize: 11, color: "#5fa86c", letterSpacing: ".1em", textTransform: "uppercase" }}>✓ Salvo</span>}
            {dirty && !savedFlash && <span style={{ fontSize: 11, color: "#b8935a", fontStyle: "italic" }}>alterações não salvas</span>}
            <button className="btn btn-sm" onClick={salvar} disabled={saving || !dirty} data-testid="lucro-save-btn">
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
          {CAMPOS.map(c => (
            <label key={c.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-3)" }}>{c.label}</span>
              <input
                type="number" step="0.01" min="0" inputMode="decimal"
                className="input"
                value={form[c.key] ?? ""}
                onChange={e => change(c.key, e.target.value)}
                placeholder="R$ 0,00"
                data-testid={`lucro-${c.key}`}
              />
            </label>
          ))}
        </div>

        {data.updated_at && (
          <div style={{ marginTop: 16, fontSize: 11, color: "var(--ink-3)", fontStyle: "italic" }}>
            Última atualização: {new Date(data.updated_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })} por {data.updated_by || "—"}
          </div>
        )}
      </div>
    </div>
  );
}
